import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'

/**
 * The part of a modal that is not markup: move focus in, lock the page
 * behind it, and put focus back where it came from on close.
 *
 * Separated from `Modal` so the component stays inside the size cap and
 * so the ordering constraints below have somewhere to be written down —
 * they are the kind of thing that gets "simplified" out of a render
 * function by someone who cannot see why they are in that order.
 */

export interface ModalBehaviour {
  /** Put this on the dialog element. It must be focusable
   * (`tabIndex={-1}`) or the opening focus move silently does nothing. */
  dialogRef: RefObject<HTMLDivElement | null>
}

export function useModalBehaviour(isOpen: boolean): ModalBehaviour {
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const openerRef = useRef<Element | null>(null)

  useEffect(() => {
    if (!isOpen) return undefined

    // Read the opener BEFORE moving focus. `document.activeElement` is the
    // only record of it — React has no notion of which button set
    // `isOpen`, and one frame later the answer is the dialog.
    openerRef.current = document.activeElement
    dialogRef.current?.focus()

    const { body } = document
    const previousOverflow = body.style.overflow
    body.style.overflow = 'hidden'

    return () => {
      // Restore the previous value rather than clearing it: a nested or
      // back-to-back dialog would otherwise unlock the page while the
      // outer one is still open.
      body.style.overflow = previousOverflow
      // Restoring focus is what makes a dialog usable twice. Without it
      // the next Tab starts from the top of the document, which on these
      // screens is forty links before you are back at the row you were
      // working on. `isConnected` because the opener is often a row
      // button that the poll behind the dialog has already replaced.
      const opener = openerRef.current
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus()
    }
  }, [isOpen])

  return { dialogRef }
}
